<template>
  <div class="h-full overflow-auto">
    <div class="w-full px-10 mt-2 text-base">
      <span class="pr-1 text-red-500">*</span>{{ t('exportDialog.excel.title.selectAttributes') }}
    </div>
    <div class="w-full mt-2">
      <!-- chooser -->
      <div class="overflow-hidden w-full px-10 mb-5">
        <tx-list-transfer
          v-model:selected-items="form.selectedItems"
          :available-items-label="t('attributeChooser.availableAttributes')"
          :selected-items-label=" t('attributeChooser.selectedAttributes')"
          :items="filteredExportableFields"
          item-key-prop="SystemName"
          item-display-prop="DisplayName"
          class="max-h-96"
          :errors="v$.checkedFields?.$errors"
          @update:selected-items="onChange"
        />
        <div class="flex justify-between mt-4">
          <tx-button type="cancel" :text="t('attributeChooser.addAll')" @click="addAll" />
          <tx-button type="cancel" :text="t('attributeChooser.removeAll')" @click="removeAll" />
        </div>
      </div>
      <div v-if="showIncludeSegmentation" class="px-10 w-full">
        <tx-switch
          v-if="userStore.activeCatalog" v-model="form.includeSegmentation" :label="t('exportDialog.excel.includeSegmentation')"
          :disabled="false" @change="onChange"
        />
      </div>
      <div v-if="showIncludeImages" class="px-10 w-full">
        <tx-switch
          v-if="userStore.activeCatalog" v-model="form.includeImages" :label="t('exportDialog.excel.includeImages')"
          :sub-label="t('exportDialog.excel.includeImagesLimit', { limit: userStore.activeCatalog.Config.ExportExcelArticlesWithImageLimit })" :disabled="false" @change="onChange"
        />
      </div>
      <div v-if="showIncludeInactiveArticles" class="flex w-full px-10">
        <tx-switch
          v-model="form.includeInactiveArticles" :label="props.source === 'requests' ? t('exportDialog.excel.includeInactiveRequests') : t('exportDialog.excel.includeInactiveArticles')"
          :disabled="false" @change="onChange"
        />
      </div>
      <div v-if="showFavoriteTagsInSeparateRow" class="flex w-full px-10">
        <tx-switch
          v-model="form.favoriteTagsInSeparateRow" :label="t('exportDialog.excel.favoriteTagsInSeparateRow')"
          :disabled="!isCheckedFieldValid" @change="onChange"
        />
      </div>
      <div v-if=" props.source === 'requests'" class="flex w-full px-10">
        <tx-switch
          v-model="form.tabularView" :label="t('exportDialog.excel.tabularView')"
          :disabled="false" @change="onChange"
        />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { useI18n } from 'vue-i18n'
import { computed, reactive, ref, watch } from 'vue'
import useVuelidate from '@vuelidate/core'
import { createI18nMessage, required } from '@vuelidate/validators'
import TxButton from '@/shared/components/TxButton.vue'
import { useUserStore } from '@/store/userData'
import TxSwitch from '@/shared/components/TxSwitch.vue'
import { AttributeType } from '@/models/catalogAttribute'
import { appConstants } from '@/models/constants'
import utils from '@/services/utils'
import TxListTransfer from '@/shared/components/txListTransfer'

interface IProps {
  source?: string
  currentStep?: number
  validationStep?: number
  showIncludeSegmentation?: boolean
  showIncludeImages?: boolean
  showIncludeInactiveArticles?: boolean
  showFavoriteTagsInSeparateRow?: boolean
  savedOptionsDataToPrefill?: Record<string, any> | undefined

}

const props = withDefaults(defineProps<IProps>(), {
  showIncludeImages: true,
  showIncludeInactiveArticles: true,
  showFavoriteTagsInSeparateRow: true,
})

const emit = defineEmits<{
  (e: 'update', val: Record<string, any>): void
}>()
const { t } = useI18n()
const userStore = useUserStore()
const withI18nMessage = createI18nMessage({ t })
const modelValue = ref<Record<string, any>>({})

const form = reactive({
  checkedFields: [] as string[],
  includeImages: false,
  includeSegmentation: false,
  includeInactiveArticles: false,
  favoriteTagsInSeparateRow: false,
  tabularView: false,
  selectedItems: [] as any[],
})
const filter = ref('')
watch(
  () => props.currentStep,
  (newStep) => {
    if (newStep === props.validationStep) {
      prefillDefaults()
    }
  },
)
const exportableFields = computed(() => {
  let exportableFields: IMyAttribute[] = []
  if (userStore.activeCatalog && userStore.myAttributes) {
    const exportableArticleProperties: IMyAttribute[] = [] // filterout unwanted fields
    const priceGroupsFields: IMyAttribute[] = []
    const favoriteTagsFields: IMyAttribute[] = []
    // Remove fields starting with underscore from exportableArticleProperties
    for (const attributeSystemName in userStore.myAttributes) {
      const attribute = userStore.myAttributes[attributeSystemName]
      if (!attributeSystemName.startsWith('_') && !attribute.IsRequest
        && !(props.source === 'requests' && attributeSystemName === 'ArticleNumber') && attribute.AttributeType !== AttributeType.Dataset) {
        exportableArticleProperties.push(attribute)
      }
    }
    if (userStore.activeCatalog?.Config.ExportAllPriceGroups) {
      // all price groups
      userStore.activeCatalog!.CatalogPriceGroupList.forEach((priceGroup) => {
        if (priceGroup.Status) {
          const priceField: IMyAttribute = Object.assign({}, appConstants.staticFieldTemplate, {
            AttributeType: AttributeType.Decimal,
            DisplayName: priceGroup.Description,
            SystemName: priceGroup.Id,
          })
          priceGroupsFields.push(priceField)
        }
      })
    }
    else {
      // my prices
      if (userStore.priceGroups && userStore.priceGroups.retail) {
        priceGroupsFields.push(userStore.myAttributes._RetailPrice)
      }
      if (userStore.priceGroups && userStore.priceGroups.wholesale) {
        priceGroupsFields.push(userStore.myAttributes._WholesalePrice)
      }
      if (userStore.priceGroups && userStore.priceGroups.outlet) {
        priceGroupsFields.push(userStore.myAttributes._OutletPrice)
      }
    }

    // favorite tags & allow it to use in lineplan catalog
    if (userStore.myAttributes && userStore.myAttributes._FavoriteTags) {
      favoriteTagsFields.push(userStore.myAttributes._FavoriteTags)
    }

    exportableFields = exportableArticleProperties.concat(priceGroupsFields, favoriteTagsFields)
    const dropReasonAttributeField: IMyAttribute = Object.assign({}, appConstants.staticFieldTemplate, {
      SystemName: 'DropReason',
      DisplayName: t('activeOrDeactiveArticle.dropReason'),
      Creatable: true,
      AttributeType: AttributeType.Nvarchar,
      ReadOnly: false,
      IsRequired: true,
    })
    exportableFields.push(dropReasonAttributeField)
    exportableFields = utils.sort(exportableFields, ['DisplayName'])
  }
  return exportableFields
})

const filteredExportableFields = computed(() => {
  return exportableFields.value.filter(field => field.DisplayName.toLowerCase().includes(filter.value.toLowerCase()))
})

const isCheckedFieldValid = computed(() => {
  return form.checkedFields.includes('_FavoriteTags')
})

const rules = computed(() => {
  const result: Record<string, any> = {}
  if (props.currentStep === props.validationStep) {
    return {
      checkedFields: {
        required: withI18nMessage(required),
      },
    }
  }
  return result
})

const v$ = useVuelidate(rules, form)
function prefillDefaults() {
  if (props.savedOptionsDataToPrefill) {
    Object.assign(form, props.savedOptionsDataToPrefill)
    onChange()
  }
}
function addAll() {
  form.selectedItems = [...filteredExportableFields.value]
  onChange()
}

function removeAll() {
  form.selectedItems = []
  onChange()
}

function onChange() {
  // set only system names in checkedFields
  if (form.selectedItems) {
    form.checkedFields = form.selectedItems.length ? form.selectedItems.map((item) => { return item.SystemName }) : []
  }
  emit('update', form)
}

function reset() {
  form.checkedFields = []
  form.includeImages = false
  form.includeSegmentation = false
  form.includeInactiveArticles = false
  form.favoriteTagsInSeparateRow = false
  form.selectedItems = []
  emit('update', form)
}

defineExpose({
  reset,
})
</script>
